<template>
  <footer class="footer">
    <div class="container">
      <div class="footer-content">
        <div class="footer-columns">
          <div class="column">
            <h3>{{ $t("FOOTER_SOCIAL") }}</h3>
            <ul>
              <li>
                <router-link to="/news">{{ $t("FOOTER_PRESS_RELEASES") }}</router-link>
              </li>
              <li v-if="BRAND_CONFIG.twitter">
                <a :href="BRAND_CONFIG.twitter" target="_blank">{{ $t("FOOTER_X") }}</a>
              </li>
              <li v-if="BRAND_CONFIG.reddit">
                <a :href="BRAND_CONFIG.reddit" target="_blank">{{ $t("FOOTER_REDDIT") }}</a>
              </li>
              <li v-if="BRAND_CONFIG.telegramGroup">
                <a :href="BRAND_CONFIG.telegramGroup" target="_blank">{{ $t("FOOTER_TELEGRAM") }}</a>
              </li>
              <li v-if="BRAND_CONFIG.telegramBot">
                <a :href="BRAND_CONFIG.telegramBot" target="_blank">{{ $t("FOOTER_TELEGRAM_BOT") }}</a>
              </li>
            </ul>
          </div>

          <div class="column">
            <h3>{{ $t("FOOTER_LEGAL") }}</h3>
            <ul>
              <li>
                <router-link to="/info/about">{{ $t("ABOUT_APPDB") }}</router-link>
              </li>
              <li>
                <router-link to="/info/tos">{{ $t("TERMS OF SERVICE") }}</router-link>
              </li>
              <li>
                <router-link to="/info/privacy">{{ $t("PRIVACY POLICY") }}</router-link>
              </li>
              <li>
                <a target="_blank" :href="`https://publisher.appdb.to/copyright-claim`">{{
                  $t("FOOTER_SUBMIT_LEGAL_CLAIM") }}</a>
              </li>
            </ul>
          </div>

          <div class="column">
            <h3>{{ $t("FOOTER_FOR_PUBLISHERS") }}</h3>
            <ul>
              <li>
                <a target="_blank"
                  href="https://rtfm.dbservices.to/#/developer-apps-publishing/journey?id=app-publication-journey-for-officially-recognised-developer">{{
                    $t("FOOTER_PUBLISH_APPS") }}</a>
              </li>
              <li>
                <a target="_blank"
                  href="https://rtfm.dbservices.to/#/services-and-features/in-app-purchases-and-subscriptions?id=implementation-of-in-app-purchases-and-in-app-subscriptions">{{
                    $t("FOOTER_MONETIZE_APPS") }}</a>
              </li>
              <li>
                <a target="_blank" href="https://publisherid.appdb.to/auth?authAppID=publisher_area">{{
                  $t("PUBLISHER_AREA") }}</a>
              </li>

              <li>
                <a target="_blank" href="https://a.appdb.to/acp.php">{{ $t("FOOTER_FOR_EMPLOYEES") }}</a>
              </li>
            </ul>
          </div>

          <div class="column">
            <h3>{{ $t("FOOTER_FOR_DEVELOPERS") }}</h3>
            <ul>
              <li>
                <a target="_blank" href="https://api.dbservices.to/v1.7/spec/">{{ $t("FOOTER_APIS") }}</a>
              </li>
              <li>
                <a target="_blank" href="https://rtfm.dbservices.to/">{{ $t("FOOTER_DOCUMENTATION") }}</a>
              </li>
              <li>
                <a target="_blank" href="https://github.com/appdb-official">{{ $t("FOOTER_SDKS_SAMPLE_APPS") }}</a>
              </li>
              <li>
                <a target="_blank" :href="`https://status.dbservices.to/#${theme}`">{{ $t("SYSTEM STATUS") }}</a>
              </li>
            </ul>
          </div>
        </div>

        <div class="language-selector">
          <button @click="langDropdownShown = !langDropdownShown" class="lang-selector-btn">
            {{ lang.name }}
            <span v-if="lang.translator" class="desc">({{ lang.translator }})</span>
          </button>
          <ul class="dropdown" v-show="langDropdownShown">
            <li v-for="(lang, key) in langs" :key="key" @click="switchLang(key)">
              {{ lang.name }}
              <span v-if="lang.translator" class="desc">({{ lang.translator }})</span>
            </li>
          </ul>
        </div>

        <div class="copyright">
          <p>© {{ BRAND_CONFIG.fromYear ? (BRAND_CONFIG.fromYear + '-') : '' }}{{ new Date().getFullYear() }}
            AYSA OU. {{ $t('NOT_AFFILIATED_NOTICE') }}
            <span v-if="BRAND_KEY !== 'appdb'">. {{ $t('POWERED_BY_APPDB') }}</span>
          </p>
        </div>

        <div class="version">
          <p v-html="$t('VERSION_BOTTOM_BAR', {
            version: $store.getters.appVersion,
          })"></p>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import { mapState } from 'vuex';

export default {
  data() {
    return {
      langDropdownShown: false,
      langs: [],
    };
  },
  computed: {
    lang() {
      return this.langs[this.$store.getters.lang] || {};
    },
    ...mapState(['theme']),
    BRAND_CONFIG() {
      return this.$store.getters.brandConfig;
    },
    BRAND_KEY() {
      return this.$store.getters.currentBrand;
    }
  },
  methods: {
    getLangs() {
      return this.$api.getLanguages().then((data) => {
        this.langs = data;
      });
    },
    switchLang(lang) {
      this.$store.dispatch('setLang', lang);
      this.langDropdownShown = false;
    },
  },
  serverPrefetch() {
    return this.getLangs();
  },
  mounted() {
    this.getLangs();

    document.body.addEventListener('click', (e) => {
      if (!e.target.closest('.lang-selector-btn')) {
        this.langDropdownShown = false;
      }
    });
  },
};
</script>

<style lang="scss" scoped>
.footer {
  margin-top: auto;
  background-color: var(--footer-top-bg);
  padding: 20px 0 15px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
}

.footer-content {
  display: flex;
  flex-direction: column;
}

.copyright {
  margin-top: 15px;
  text-align: center;

  p {
    color: $footer-text-color;
    font-size: 12px;
    margin: 0;
    line-height: 1.3;
  }
}

.version {
  text-align: center;
  margin-top: 10px;

  p {
    color: $footer-text-color;
    font-size: 12px;
    margin: 0;
    line-height: 1.3;
  }
}

.footer-columns {
  display: flex;
  flex-wrap: wrap;
}

.column {
  min-width: 150px;
  margin-bottom: 15px;
  margin-right: 20px;
  flex: 1;

  h3 {
    color: $footer-link-color;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 10px;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      margin-bottom: 8px;
      position: relative;

      a,
      button {
        color: $footer-link-color;
        font-size: 12px;
        text-decoration: none;
        transition: opacity 0.2s;

        &:hover {
          opacity: 0.7;
        }
      }

      button {
        background: none;
        border: none;
        padding: 0;
        font: inherit;
        cursor: pointer;
        display: flex;
        align-items: center;
      }
    }
  }
}

.language-selector {
  display: flex;
  justify-content: center;
  position: relative;
  margin-top: 20px;
  padding-top: 15px;
  margin-bottom: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.lang-selector-btn {
  background: none;
  border: none;
  color: $footer-link-color;
  font-size: 12px;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 3px;
  border: 1px solid $footer-button-border-color;

  &:hover {
    opacity: 0.7;
  }
}

.dropdown {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: var(--secondary-bg-color, var(--footer-top-bg));
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.2));
  border-radius: 6px;
  padding: 8px 0;
  z-index: 100;
  min-width: 160px;
  margin-bottom: 5px;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.15);
  max-height: 300px;
  overflow-y: auto;

  li {
    padding: 8px 12px;
    font-size: 12px;
    cursor: pointer;
    color: var(--text-color, $footer-link-color);
    transition: background-color 0.2s;

    &:hover {
      background-color: var(--hover-bg-color, rgba(0, 0, 0, 0.05));
    }
  }
}

.desc {
  font-size: 10px;
  opacity: 0.7;
  margin-left: 3px;
}

@media (max-width: 767px) {
  .footer {
    padding: 20px 0;
  }

  .footer-columns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
  }

  .column {
    margin-right: 0;
    min-width: 0;
    margin-bottom: 0;

    h3 {
      margin-bottom: 8px;
    }
  }

  .language-selector {
    margin-top: 15px;
    padding-top: 10px;
  }
}

@media (max-width: 480px) {
  .footer {
    padding: 15px 0;
  }

  .copyright {
    margin-bottom: 15px;
  }
}
</style>
